import { ReactNode } from 'react'
import type { TableNode } from '@table-library/react-table-library/types/table'
import { useTranslation } from 'libs/i18n'
import { ContextMenu } from 'shared/components'
import type { Option } from 'shared/components/ContextMenu/types'
import type { CardTableColumn } from 'styles/table'
import { useCouponDetails } from 'features/coupons/hooks/useCouponDetails'
import type { CouponItem } from 'features/coupons/types/coupons.types'

type UseColumnsProps = {
	getMenuOptions: (coupon: CouponItem) => Option[]
}

const textClassName = 'block truncate text-[13px] text-muted-foreground'
const numberClassName = 'block text-right text-[13px] tabular-nums text-muted-foreground'
const padding = '12px 16px'

const cell = (render: (coupon: CouponItem) => ReactNode) => (node: TableNode) =>
	render(node as unknown as CouponItem)

export const useColumns = ({ getMenuOptions }: UseColumnsProps): CardTableColumn[] => {
	const { t } = useTranslation()
	const getDetails = useCouponDetails()

	return [
		{
			label: t('Code'),
			width: 'minmax(140px, 1.5fr)',
			padding,
			renderCell: cell((item) => (
				<span className="block truncate text-[13px] font-semibold text-foreground">
					{item.code}
				</span>
			)),
			select: true,
			pinLeft: true
		},
		{
			label: t('Status'),
			width: 'minmax(100px, 1.1fr)',
			padding,
			renderCell: cell((item) => <span className={textClassName}>{getDetails(item).status}</span>)
		},
		{
			label: t('Price discount'),
			width: 'minmax(120px, 1.75fr)',
			padding,
			align: 'right',
			renderCell: cell((item) => (
				<span className={numberClassName}>{getDetails(item).discount}</span>
			))
		},
		{
			label: t('Booking window'),
			width: 'minmax(190px, 2.55fr)',
			padding,
			renderCell: cell((item) => (
				<span className={textClassName}>{getDetails(item).bookingWindow}</span>
			))
		},
		{
			label: t('# Used'),
			width: 'minmax(80px, 1fr)',
			padding,
			align: 'right',
			renderCell: cell((item) => <span className={numberClassName}>{item.times_used}</span>)
		},
		{
			label: t('Max uses'),
			width: 'minmax(90px, 1.25fr)',
			padding,
			align: 'right',
			renderCell: cell((item) => (
				<span className={numberClassName}>{getDetails(item).maxUses}</span>
			))
		},
		{
			label: t('Expires'),
			width: 'minmax(110px, 1.5fr)',
			padding,
			renderCell: cell((item) => <span className={textClassName}>{getDetails(item).expires}</span>)
		},
		{
			label: '',
			width: '48px',
			padding: '12px 8px',
			renderCell: cell((item) => (
				<ContextMenu
					dataId={item.id}
					item={item}
					options={getMenuOptions(item)}
					label={t('Actions for {{ name }}', { name: item.code })}
				/>
			))
		}
	]
}
